import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

/**
 * Heuristic: does this plain-text paste look like Markdown?
 *
 * Conservative — needs at least 2 markdown signals to convert, so plain prose
 * with a stray `**` is NOT treated as markdown. Ported verbatim from the Lexical
 * editor's markdown-paste-plugin (engine-neutral regex scoring).
 */
interface MarkdownScore {
  /** Total weighted markdown-signal score. */
  score: number;
  /** Count of lines that are ATX headings (`# ...`). */
  headings: number;
  /** Count of pipe-table rows (`| ... |`). */
  tableRows: number;
  /** Count of bullet/ordered list lines. */
  listItems: number;
}

/** Tally markdown signals in `text` (shared by the boolean predicates below). */
export function markdownScore(text: string): MarkdownScore {
  const lines = text.split('\n');
  let score = 0;
  let headings = 0;
  let tableRows = 0;
  let listItems = 0;
  for (const line of lines) {
    const t = line.trim();
    if (/^\|.+\|$/.test(t)) { score += 2; tableRows++; } // table row
    if (/^#{1,6}\s/.test(t)) { score++; headings++; } // heading
    if (/^[-*+]\s/.test(t)) { score++; listItems++; } // bullet
    if (/^\d+\.\s/.test(t)) { score++; listItems++; } // ordered
    if (/^>\s/.test(t)) score++; // quote
    if (/^```/.test(t)) score++; // fenced code
    if (/^---\s*$/.test(t)) score++; // hr
    if (/\*\*.+\*\*/.test(t)) score++; // bold
    if (/\[.+\]\(.+\)/.test(t)) score++; // link
    if (/^- \[[ x]\]/.test(t)) score++; // task item
    if (/!\[.+\]\(.+\)/.test(t)) score += 2; // image — unambiguously markdown
  }
  return { score, headings, tableRows, listItems };
}

/**
 * Heuristic: does this plain-text paste look like Markdown? Conservative —
 * needs at least 2 markdown signals, so plain prose with a stray `**` is not
 * treated as markdown. Ported from the Lexical markdown-paste-plugin.
 */
export function looksLikeMarkdown(text: string): boolean {
  return markdownScore(text).score >= 2;
}

/**
 * Does this text look UNMISTAKABLY like a markdown *document* (not source code
 * that merely trips the loose heuristic)? Used to override the
 * highlighted-code-HTML signal: a `.md` file copied from an editor arrives as
 * the same `<pre>`+colored-span HTML as code, so structure alone can't tell
 * them apart — but genuine markdown is dense with heading/table/list signals
 * that source code is not.
 *
 * True only on ATX headings (`# ...`) or pipe-tables (`| ... |`) — structures
 * that essentially never appear in source code. We deliberately do NOT count
 * list density: JSDoc / block-comment lines start with `*` (and sometimes `-`),
 * so a comment-heavy source file scores several "bullets" and would otherwise
 * be misread as markdown. Headings + tables are the signals code can't fake.
 *
 * Trade-off: a bullet-ONLY markdown snippet (no heading/table) copied from an
 * editor as highlighted HTML will land as a code block. That's rare and far
 * less harmful than the common case (pasting source code) breaking.
 */
export function looksStronglyLikeMarkdown(text: string): boolean {
  const { headings, tableRows } = markdownScore(text);
  return headings > 0 || tableRows > 0;
}

/**
 * Extract the text inside the first `<pre>` of an HTML string. Many sources
 * (VS Code, GitHub, markdown editors) encode a copied markdown block as a
 * `<pre>` in `text/html`; ProseMirror's clipboard parser would import that as a
 * code block. We pull the raw text back out so it can be parsed as markdown.
 */
function preText(html: string): string {
  const match = /<pre[^>]*>([\s\S]*?)<\/pre>/i.exec(html);
  if (!match) return '';
  return match[1]
    .replace(/<[^>]+>/g, '') // strip any inner tags (e.g. <code>, <span>)
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&'); // unescape last so &amp;lt; → &lt; → <
}

/**
 * Choose the markdown source from a paste's clipboard payloads. Prefers
 * `text/plain` when it already looks like markdown; otherwise falls back to the
 * text inside a `<pre>` in `text/html` (the shape the first paste event often
 * carries). Returns null when neither looks like markdown.
 */
/**
 * The source language of a code-editor paste, from the `vscode-editor-data`
 * clipboard payload, or null when it isn't an explicit code paste.
 *
 * VS Code (and editors that mimic its clipboard, incl. our own code blocks)
 * write `vscode-editor-data: {"mode":"<language>"}`. We return the mode ONLY
 * when it's a real programming language — `markdown` (and a missing/empty mode)
 * returns null so the paste flows through the normal markdown heuristic instead
 * of being forced into a code block.
 */
export function codeLanguageFromClipboard(vscodeEditorData: string): string | null {
  if (!vscodeEditorData) return null;
  let mode: string | undefined;
  try {
    mode = (JSON.parse(vscodeEditorData) as { mode?: string }).mode;
  } catch {
    return null;
  }
  if (!mode || mode === 'markdown' || mode === 'md' || mode === 'plaintext') return null;
  return mode;
}

/**
 * True when `html` is syntax-highlighted code from an editor: a `<pre>`
 * containing color-styled `<span>` tokens. This is the clipboard signature of
 * code editors (VS Code, Zed, etc.) that don't set `vscode-editor-data`.
 *
 * It deliberately does NOT match a bare `<pre>` of plain text (the shape used
 * when markdown source is copied into a <pre>) — only a <pre> whose content was
 * tokenized into styled spans, which is unambiguously code. This lets us treat
 * such a paste as code even when its text trips the markdown heuristic (JSDoc
 * `*` / comment `-` lines), without misclassifying markdown-in-a-<pre>.
 */
export function htmlIsHighlightedCode(html: string): boolean {
  if (!html) return false;
  const pre = /<pre[\s\S]*?<\/pre>/i.exec(html)?.[0];
  if (!pre) return false;
  // A styled span inside the <pre> = syntax highlighting = code.
  return /<span[^>]*\bstyle\s*=\s*["'][^"']*color\s*:/i.test(pre);
}

export function pickMarkdownSource(plain: string, html: string): string | null {
  if (plain && looksLikeMarkdown(plain)) return plain;
  const fromPre = html ? preText(html) : '';
  if (fromPre && looksLikeMarkdown(fromPre)) return fromPre;
  return null;
}

// Minimal JSONContent shape we walk when sanitizing parsed markdown.
interface MdMark {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attrs?: Record<string, any>;
}
interface MdNode {
  type?: string;
  marks?: MdMark[];
  content?: MdNode[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

/**
 * The set of node types that are valid block children of `listItem`/`taskItem`.
 * ProseMirror's content expression for both is `paragraph block*`.
 */
const BLOCK_TYPES = new Set([
  'paragraph', 'bulletList', 'orderedList', 'taskList',
  'codeBlock', 'blockquote', 'horizontalRule', 'heading',
]);

/**
 * Inline node types that can appear directly under listItem when
 * `@tiptap/markdown` emits continuation text after a code block inside a list.
 * These must be wrapped in a paragraph before insertion.
 */
const INLINE_TYPES = new Set(['text', 'hardBreak']);

/**
 * Sanitize a parsed markdown JSON tree before inserting into the editor.
 *
 * Two fixes:
 * 1. `code` mark exclusion: ProseMirror's `code` mark has `excludes: '_'` —
 *    it cannot coexist with any other mark. `@tiptap/markdown` sometimes emits
 *    `[bold, code]` on the same text node (e.g. `**\`DocBridge\`**`). Keep only
 *    `code`, drop the rest.
 *
 * 2. Bare inline nodes in list items: when a list item contains a fenced code
 *    block followed by continuation prose, `@tiptap/markdown` can emit the
 *    trailing text as a bare `text` node directly under `listItem`. But
 *    `listItem` content is `paragraph block*` — text nodes are not blocks.
 *    Collect consecutive inline nodes and wrap them in a `paragraph`.
 */
export function sanitizeMarks(node: MdNode): MdNode {
  const out: MdNode = { ...node };

  // Fix 1: code mark exclusion on text nodes
  if (Array.isArray(node.marks) && node.marks.some((m) => m.type === 'code') && node.marks.length > 1) {
    out.marks = node.marks.filter((m) => m.type === 'code');
  }

  if (Array.isArray(node.content)) {
    const sanitizedChildren = node.content.map(sanitizeMarks);

    // Fix 2: wrap bare inline nodes inside listItem/taskItem into paragraphs
    if (node.type === 'listItem' || node.type === 'taskItem') {
      const fixed: MdNode[] = [];
      let pendingInlines: MdNode[] = [];

      for (const child of sanitizedChildren) {
        if (INLINE_TYPES.has(child.type ?? '')) {
          pendingInlines.push(child);
        } else {
          if (pendingInlines.length > 0) {
            fixed.push({ type: 'paragraph', content: pendingInlines });
            pendingInlines = [];
          }
          if (!BLOCK_TYPES.has(child.type ?? '')) {
            // Unknown non-block type — wrap defensively
            fixed.push({ type: 'paragraph', content: [child] });
          } else {
            fixed.push(child);
          }
        }
      }
      if (pendingInlines.length > 0) {
        fixed.push({ type: 'paragraph', content: pendingInlines });
      }
      out.content = fixed;
    } else {
      out.content = sanitizedChildren;
    }
  }

  return out;
}

const markdownPasteKey = new PluginKey('markdownPaste');

/**
 * Intercepts a paste whose source looks like Markdown and converts it to
 * formatted nodes, rather than letting it land as raw `#`/`-`/`**` text or — the
 * bug this fixes — a single code block. Requires the `Markdown` extension to be
 * registered (it provides the `contentType: 'markdown'` insert path).
 *
 * Why it works (see ProseMirror `prosemirror-view` input.ts `doPaste`):
 * `handlePaste` runs BEFORE ProseMirror's clipboard parser. Returning `true`
 * short-circuits `doPaste`, so neither CodeBlock's `parseHTML: [{tag:'pre'}]`
 * rule (which would import copied-markdown `<pre>` HTML as a code block) nor its
 * lower-priority `codeBlockVSCodeHandler` ever runs. `priority: 1000` puts this
 * plugin ahead of CodeBlock's default-100 handler so we win on the first event.
 * We never call `event.preventDefault()` — ProseMirror does that for us when a
 * `handlePaste` returns truthy.
 *
 * Source selection (matches the Lexical markdown-paste-plugin): prefer the
 * markdown source over its rendered HTML. GitHub / VS Code / our own editor emit
 * both; the source is higher fidelity. The first paste event often carries the
 * source only in `text/html` as a `<pre>`, so {@link pickMarkdownSource} checks
 * both payloads. Defers (returns false) when neither looks like markdown.
 */
export const MarkdownPaste = Extension.create({
  name: 'markdownPaste',

  // Tiptap sorts extensions by priority (desc), and ProseMirror runs their
  // handlePaste plugins in that order. StarterKit's CodeBlock
  // `codeBlockVSCodeHandler` has default priority (100); a higher priority here
  // makes our markdown handler run FIRST and win for markdown source pastes.
  priority: 1000,

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: markdownPasteKey,
        props: {
          handlePaste(view: EditorView, event: ClipboardEvent) {
            const clipboard = event.clipboardData;
            if (!clipboard) return false;

            const plain = clipboard.getData('text/plain');
            const html = clipboard.getData('text/html');
            const vsData = clipboard.getData('vscode-editor-data');

            // Insert `text` as a literal code block (whitespace + line breaks
            // preserved), replacing the current block. Returns false if the
            // schema has no codeBlock node.
            const insertCodeBlock = (text: string, language: string | null) => {
              const { state, dispatch } = view;
              const { $from } = state.selection;
              const codeBlockType = state.schema.nodes.codeBlock;
              if (!codeBlockType) return false;
              const normalized = text.replace(/\r\n/g, '\n');
              const node = codeBlockType.create(
                language ? { language } : null,
                state.schema.text(normalized),
              );
              dispatch(state.tr.replaceWith($from.before($from.depth), $from.after($from.depth), node));
              return true;
            };

            // EXPLICIT code source wins over the markdown text heuristic. A paste
            // copied from a code editor carries `vscode-editor-data` whose `mode`
            // is the source language. When that mode is a real programming
            // language (not markdown), insert as a literal code block — never
            // markdown-parse it. Without this, source whose comment lines start
            // with `*`/`-` (JSDoc, block comments) trips looksLikeMarkdown and
            // gets reflowed, destroying indentation and line breaks.
            const codeLang = codeLanguageFromClipboard(vsData);
            if (codeLang && plain && insertCodeBlock(plain, codeLang)) return true;

            // Editors that DON'T set vscode-editor-data (e.g. Zed) still emit a
            // <pre> of color-styled <span> tokens in text/html — a "this is code"
            // signal. Honor it over the loose markdown heuristic (false-positives
            // on JSDoc `*` / comment `-` lines), inserting the clean text/plain so
            // indentation survives — UNLESS the text is unmistakably a markdown
            // document. A .md file copied from an editor is highlighted into the
            // SAME <pre>+span HTML as code, so we can't tell them apart by
            // structure; the strong markdown signals (headings/tables/dense
            // lists) decide, and markdown wins so it converts to rich text.
            // Same rich-document guard as the fallback below: if the HTML contains
            // block-level elements alongside the styled <pre>, it's a web page
            // (e.g. Claude chat, GitHub) not a bare code-editor paste — let
            // ProseMirror's HTML parser handle the full structure.
            const htmlIsRichDocument = /<h[1-6][\s>]|<ul[\s>]|<ol[\s>]|<blockquote[\s>]/i.test(html);
            if (
              plain &&
              htmlIsHighlightedCode(html) &&
              !htmlIsRichDocument &&
              !looksStronglyLikeMarkdown(plain) &&
              insertCodeBlock(plain, codeLang)
            ) {
              return true;
            }

            // Pick the markdown source. ProseMirror fires paste through up to two
            // events (the real OS paste, then a `capturePaste` fallback ~50ms
            // later). On the first event the markdown source frequently rides in
            // text/html as a <pre> while text/plain is the rendered text. Check
            // both: prefer text/plain when it looks like markdown, otherwise fall
            // back to the text inside a <pre> in text/html.
            const md = pickMarkdownSource(plain, html);
            if (!md) {
              // Multi-line plain text from a syntax-highlighted source (VS Code,
              // Claude, GitHub) — the HTML has a <pre> but plain text isn't markdown.
              // Insert as a code block so \n is preserved.
              // EXCEPTION: when html is full rich HTML (has block-level elements like
              // headings or lists alongside the <pre>), return false so ProseMirror's
              // HTML clipboard parser handles it correctly — it will produce headings,
              // lists, and code blocks from the structure. The <pre>-only check is too
              // broad: a web page with mixed content (e.g. a Claude chat response) has
              // <pre> tags inside otherwise-rich HTML, and we must not collapse it all
              // into a single code block.
              if (plain && plain.includes('\n') && html.includes('<pre') && !htmlIsRichDocument) {
                if (insertCodeBlock(plain, codeLang)) return true;
              }
              return false;
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const manager = (editor.storage as any).markdown?.manager;
            if (!manager) return false;

            let doc: MdNode;
            try {
              doc = manager.parse(md) as MdNode;
            } catch {
              return false; // fall back to default paste on parse failure
            }
            if (!doc) return false;

            // Strip illegal mark combos (e.g. bold+code) the parser may emit;
            // otherwise insertContent throws and the paste aborts to a code block.
            const sanitized = sanitizeMarks(doc);
            // manager.parse returns a full {type:'doc'} node — insert its block
            // content array, not the doc wrapper.
            const content = sanitized.type === 'doc' ? (sanitized.content ?? []) : sanitized;

            // Returning true short-circuits doPaste BEFORE ProseMirror's clipboard
            // parser runs CodeBlock's <pre>→codeBlock rule and before CodeBlock's
            // lower-priority vscode handler. priority:1000 keeps us first. We never
            // call preventDefault — ProseMirror does that when we return true.
            //
            // Safety net: sanitizeMarks fixes the known invalid shapes the parser
            // emits (bold+code marks, bare inline nodes in list items). If some
            // other unanticipated shape still fails schema validation, swallow the
            // throw and return false so ProseMirror's default paste handles it,
            // rather than aborting with an uncaught RangeError.
            try {
              editor.chain().focus().insertContent(content).run();
            } catch {
              return false;
            }
            return true;
          },
        },
      }),
    ];
  },
});
